import { type SilentAuthResponse } from '@ligonier/shared'
import type { DonateClickDetail } from '@ligonier/shared/utils/events'
import type { SearchSelectDetail } from '@ligonier/shared/domain/search'
import { type NavigationItem } from './props.js'

export type { DonateClickDetail, SearchSelectDetail }

export interface MenuClickDetail {
  open: boolean
}

export interface SearchSubmitDetail {
  query: string
}

export interface NavLoadDetail {
  items: NavigationItem[]
  /** True when fetched items were immediately applied to navItems.
   * False when items were staged for deferred apply (menu was open), applied on close. */
  applied: boolean
}

export interface NavErrorDetail {
  error: unknown
}

export interface EmailSignupDetail {
  email: string
}

export interface HeaderEvents {
  'menu-click': CustomEvent<MenuClickDetail>
  'menu-close': CustomEvent<void>
  'logo-click': CustomEvent<void>
  'partner-click': CustomEvent<void>
  /** Cancelable — call preventDefault() to suppress the default donate navigation.
   * Shared with the footer's callout give CTAs: `detail` is undefined for header
   * clicks and carries source/intent for footer-originated ones. */
  'donate-click': CustomEvent<DonateClickDetail | undefined>
  'search-submit': CustomEvent<SearchSubmitDetail>
  'search-select': CustomEvent<SearchSelectDetail>
  'auth-status': CustomEvent<SilentAuthResponse>
  /** Fired when the account icon is clicked while the user is not authenticated.
   * Cancelable — call preventDefault() to suppress the default login redirect. */
  'auth-login-click': CustomEvent<void>
  /** Fired after logout. Local session is always cleared even if the server call failed. */
  'auth-logout': CustomEvent<void>
  'nav-load': CustomEvent<NavLoadDetail>
  'nav-error': CustomEvent<NavErrorDetail>
  /** Fired when the menu's "Stay in Touch" email signup form is submitted. */
  'email-signup': CustomEvent<EmailSignupDetail>
}

export type HeaderEventName = keyof HeaderEvents

/** Typed constants for all custom event names emitted by LigonierHeader. */
export const EVENTS = {
  MENU_CLICK: 'menu-click',
  MENU_CLOSE: 'menu-close',
  LOGO_CLICK: 'logo-click',
  PARTNER_CLICK: 'partner-click',
  DONATE_CLICK: 'donate-click',
  SEARCH_SUBMIT: 'search-submit',
  SEARCH_SELECT: 'search-select',
  AUTH_STATUS: 'auth-status',
  AUTH_LOGIN_CLICK: 'auth-login-click',
  AUTH_LOGOUT: 'auth-logout',
  NAV_LOAD: 'nav-load',
  NAV_ERROR: 'nav-error',
  EMAIL_SIGNUP: 'email-signup',
} as const satisfies Record<string, HeaderEventName>

declare global {
  interface HTMLElementEventMap extends HeaderEvents { }
}
